/**
 * Focus rings only for the keyboard: the last real input, on
 * `<html data-input-modality>`.
 *
 * The theme draws its focus ring, a 1px outline, on whatever matches `:focus-visible`, and
 * leaves that call to the browser. Every drawer and overlay here (cart, wishlist, account,
 * search, the menu, the country list) hands focus back to what had it before it opened. Focus
 * moved by a script or a dialog is where the browsers guess, and guess differently:
 *
 *   - Chrome counts any key as keyboard use, Escape included. Click the cart, press Escape, and
 *     the cart button comes back ringed.
 *   - Safari counts focus moved by a script or a dialog as keyboard focus, and it does focus a
 *     tapped <summary>, which the menu button is. After the menu has been used, whichever
 *     overlay closes next rings the menu button.
 *
 * Fixing those paths one at a time only uncovered the next, so the call is made here, once, for
 * the whole site. A pointer (mouse, trackpad, touch, pen) sets `pointer`. Tab, Enter or Space
 * on a control, arrows within a radio group, slider or the like, and up or down through a
 * combobox's list set `keyboard`. Escape, shortcuts, scrolling keys and typing into a field
 * leave it as it is: pressing Escape does not mean the shopper got there by keyboard.
 * `snippets/input-modality.liquid` hides the ring while it reads `pointer`.
 *
 * Focus itself still moves exactly as before, so keyboard and screen-reader users keep their
 * place, and a keyboard user still sees the ring. Before the first input the attribute is
 * absent and the browser decides, as it did before.
 */

export const ATTRIBUTE = "data-input-modality";

/** Keys that press the control that has focus. Tab counts wherever it lands. */
const PRESS_KEYS = new Set(["Enter", " "]);

/**
 * Keys that move through a composite control (radio group, slider, tabs, a listbox), and
 * otherwise only scroll: the cart drawer puts focus on its close button, and arrowing down the
 * drawer from there is still reading with a mouse in hand.
 */
const MOVE_KEYS = new Set([
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
  "PageUp",
  "PageDown",
]);
const COMPOSITE_SELECTOR =
  "input[type='radio'], input[type='range'], [role='radio'], [role='slider'], [role='tab'], [role='option'], [role='menuitem'], [role='gridcell'], [role='treeitem'], [role='spinbutton']";

/**
 * A combobox keeps focus in its text field while up and down move through its list, as in
 * the search overlay's predictive results. Every other key there is typing or the caret.
 */
const LIST_KEYS = new Set(["ArrowUp", "ArrowDown"]);
const COMBOBOX_SELECTOR = "[role='combobox']";
const LISTBOX_SELECTOR = "[role='listbox']";

/** Inputs that take no typing, so keys on them are keyboard use like on a button. */
const UNTYPED_INPUTS = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

/**
 * Whether keys on `element` go into it as text, or choose from it, rather than move the page.
 *
 * @param {Element | null | undefined} element
 * @returns {boolean}
 */
export function takesTyping(element) {
  if (!element || typeof element.matches !== "function") return false;
  if (element.isContentEditable) return true;
  if (element.matches("textarea, select")) return true;

  return element.matches("input") && !UNTYPED_INPUTS.has(element.type);
}

/**
 * Whether `element` is a combobox: it says so with its role, or it controls a listbox.
 *
 * @param {Element} element
 * @param {Document} [doc]
 * @returns {boolean}
 */
export function isCombobox(element, doc = globalThis.document) {
  if (element.matches(COMBOBOX_SELECTOR)) return true;

  const controls = element.getAttribute?.("aria-controls")?.split(/\s+/) ?? [];

  return controls.some(
    (id) => id && doc?.getElementById?.(id)?.matches?.(LISTBOX_SELECTOR),
  );
}

/**
 * The input an event shows, or null when it shows neither.
 *
 * @param {{type: string, key?: string, ctrlKey?: boolean, metaKey?: boolean, altKey?: boolean,
 *   target?: EventTarget | null, composedPath?: () => EventTarget[]}} event
 * @param {Document} [doc]
 * @returns {'pointer' | 'keyboard' | null}
 */
export function modalityFor(event, doc = globalThis.document) {
  if (event.type === "pointerdown") return "pointer";
  if (event.type !== "keydown") return null;
  if (event.ctrlKey || event.metaKey || event.altKey) return null;
  if (event.key === "Tab") return "keyboard";

  const pressing = PRESS_KEYS.has(event.key);

  if (!pressing && !MOVE_KEYS.has(event.key)) return null;

  // Inside an open shadow root, `target` is only the host.
  const target = event.composedPath?.()[0] ?? event.target;

  if (typeof target?.matches !== "function") return null;

  // Nothing focused, or only a dialog: Space and the arrows scroll, and Enter does nothing.
  if (target === doc?.body || target === doc?.documentElement) return null;
  if (target.matches("dialog")) return null;
  if (LIST_KEYS.has(event.key) && isCombobox(target, doc)) return "keyboard";
  if (takesTyping(target)) return null;
  if (pressing) return "keyboard";

  return target.matches(COMPOSITE_SELECTOR) ? "keyboard" : null;
}

/**
 * Keep `ATTRIBUTE` on `doc`'s root in step with the last real input. Listens in the capture
 * phase, so a component that stops an event cannot hide it.
 *
 * @param {Document} [doc]
 * @returns {() => void} Stops listening.
 */
export function trackModality(doc = globalThis.document) {
  const onInput = (event) => {
    const modality = modalityFor(event, doc);

    if (modality && doc.documentElement.getAttribute(ATTRIBUTE) !== modality) {
      doc.documentElement.setAttribute(ATTRIBUTE, modality);
    }
  };
  const options = { capture: true, passive: true };

  doc.addEventListener("pointerdown", onInput, options);
  doc.addEventListener("keydown", onInput, options);

  return () => {
    doc.removeEventListener("pointerdown", onInput, options);
    doc.removeEventListener("keydown", onInput, options);
  };
}

if (typeof document !== "undefined") {
  trackModality();
}
